import { useEffect, useRef, useState } from 'react'
import Box from '@mui/material/Box'
import Drawer from '@mui/material/Drawer'
import { Outlet, useLocation } from 'react-router-dom'
import { SidebarContent } from './SidebarContent'
import { Topbar } from './Topbar'
import { PageChromeProvider } from './pageChrome'
import { layout } from '../../styles/theme'
import { prefetchMapa } from '@/core/router/prefetchMapa'
import { useI18n } from '@/core/i18n/useI18n'
import { AssistenteColuna } from '@/features/assistente/AssistenteColuna'
import { useAuth } from '@/core/auth/useAuth'
import { AvisoProgramacao } from './AvisoProgramacao'

const WIDTH = layout.sidebarWidth
const COMPACT = layout.sidebarCompact
const COLLAPSE_KEY = 'sidebar-collapsed'

/** Telas em que o mapa é o conteúdo e a navegação é acessório. */
const MAP_ROUTES = ['/logistica/mapa']

function lerPreferenciaRecolhida(): boolean {
  try {
    return localStorage.getItem(COLLAPSE_KEY) === '1'
  } catch {
    return false
  }
}

/** Casca do app: sidebar recolhível (248↔64) + topbar (64) + conteúdo com entrada animada. */
export function AppShell() {
  const { t } = useI18n()
  const { user } = useAuth()
  const isStaff = Boolean(user?.is_staff)
  const location = useLocation()
  const noMapa = MAP_ROUTES.includes(location.pathname)
  const [mobileOpen, setMobileOpen] = useState(false)
  // Entrar direto no Mapa (link, recarregar a página) já nasce recolhido.
  const [collapsed, setCollapsed] = useState(() => noMapa || lerPreferenciaRecolhida())
  const eraNoMapa = useRef(noMapa)

  // O chunk do mapbox-gl tem 537 KB e, sendo lazy, só começava a baixar NO CLIQUE
  // em "Mapa" — 1,4 s de rede antes de a tela aparecer. Buscá-lo em tempo ocioso,
  // com a casca já de pé, tira esse tempo do caminho crítico: quando o clique vem,
  // o módulo já está no registro. Fora da tela de login de propósito. O portal não
  // tem mais o Mapa desde 03/11/2026, e o cliente não baixa o chunk à toa.
  useEffect(() => {
    if (isStaff) prefetchMapa()
  }, [isStaff])

  // O Mapa recolhe a navegação sozinho: ali o mapa é o conteúdo, e cada pixel de
  // largura conta. Só na TRANSIÇÃO entre estar e não estar no Mapa — quem abrir a
  // barra à mão enquanto navega no mapa não é contrariado no render seguinte.
  //
  // Recolher aqui NÃO grava a preferência. Ao sair, a barra volta ao que o usuário
  // escolheu, senão passar uma vez pelo Mapa deixaria o sistema inteiro recolhido.
  // Se ele usou o botão da barra, o toggle gravou, e é esse valor que volta.
  useEffect(() => {
    if (noMapa === eraNoMapa.current) return
    eraNoMapa.current = noMapa
    setCollapsed(noMapa ? true : lerPreferenciaRecolhida())
  }, [noMapa])

  const width = collapsed ? COMPACT : WIDTH
  // Rotas full-bleed: o conteúdo ocupa toda a área (sem padding/limite) — Mapa e os
  // kanbans (Solicitações/Cargas), que precisam de 100% da tela. As páginas full-bleed
  // dão o próprio padding (o shell não aplica p/maxWidth aqui).
  const fullBleed = [
    '/logistica/mapa',
    '/logistica/dashboard',
    '/logistica/pendencias',
    '/logistica/tickets', '/logistica/shipments', '/logistica/transporte-maquina',
    '/logistica/outros-servicos-transporte',
    // Painel de Jornada: as duas telas dão o próprio padding. Ficar de fora daqui
    // somava o padding do shell ao da página e abria uma faixa morta de 32px entre
    // a topbar e os indicadores, que nenhuma tela vizinha tem.
    '/logistica/jornada', '/logistica/jornada/historico',
    '/logistica/diarias', '/logistica/diarias/lancamento', '/logistica/diarias/conferencia',
    '/portal/tickets', '/portal/cargas',
    // Início do portal: cockpit de indicadores, mesmo desenho do dashboard.
    '/portal',
  ].includes(location.pathname)

  function toggleCollapse() {
    setCollapsed((c) => {
      const next = !c
      try {
        localStorage.setItem(COLLAPSE_KEY, next ? '1' : '0')
      } catch {
        /* ignore */
      }
      return next
    })
  }

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: 'background.default' }}>
      {/* Sidebar permanente (md+) */}
      <Box
        component="nav"
        sx={{ width: { md: width }, flexShrink: { md: 0 }, transition: 'width .2s ease' }}
        aria-label={t('layout.appShell.mainNavigation')}
      >
        <Drawer
          variant="permanent"
          open
          sx={{
            display: { xs: 'none', md: 'block' },
            '& .MuiDrawer-paper': {
              width,
              boxSizing: 'border-box',
              borderRight: '1px solid',
              borderColor: 'divider',
              overflowX: 'hidden',
              transition: 'width .2s ease',
            },
          }}
        >
          <SidebarContent collapsed={collapsed} onToggleCollapse={toggleCollapse} />
        </Drawer>

        {/* Sidebar mobile (temporária, sempre expandida) */}
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={() => setMobileOpen(false)}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': { width: WIDTH, boxSizing: 'border-box' },
          }}
        >
          <SidebarContent onNavigate={() => setMobileOpen(false)} />
        </Drawer>
      </Box>

      {/* Coluna principal */}
      <PageChromeProvider>
        <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
          <Topbar onMenuClick={() => setMobileOpen(true)} />
          <Box
            component="main"
            key={location.pathname}
            className="anim-page-in"
            sx={
              fullBleed
                ? // Full-bleed (kanban/mapa): a página dona da densidade; coluna flex p/ os
                  // filhos esticarem a 100% da largura automaticamente e preencherem a altura.
                  { flex: 1, minHeight: 0, minWidth: 0, display: 'flex', flexDirection: 'column', width: '100%' }
                : // Padrão do sistema: largura cheia com padding (SEM cap de 1280). Telas que
                  // querem um miolo estreito (forms) definem o próprio maxWidth no card.
                  { flex: 1, p: { xs: 2, md: 4 }, width: '100%', minWidth: 0 }
            }
          >
            <Outlet />
          </Box>
        </Box>

        {/* Assistente: terceira coluna, irmã da principal e fora do Outlet, para a
            conversa sobreviver à navegação. Empurra o conteúdo em md+, vira gaveta
            abaixo disso, e só existe para quem tem a permissão com o assistente
            ligado no runtime. */}
        <AssistenteColuna />
        {/* Programação nova do cliente: aviso em tela cheia para a dupla do contrato. */}
        {isStaff && <AvisoProgramacao />}
      </PageChromeProvider>
    </Box>
  )
}
